import type { Draft, PayloadAction, Slice } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import { fetchCurrency } from "@store/slices/cartSlice";
import { addFreeTrialProductToCart } from "@store/slices/cartSlice/actions/addProductToCart";
import { fetchTrialCart } from "@store/slices/cartSlice/actions/fetchCart";
import { fetchTrialCartItems } from "@store/slices/cartSlice/actions/fetchCartItems";
import { fetchTrialCartPrices } from "@store/slices/cartSlice/actions/fetchCartPrices";
import { setFreeTrialShippingMethodsOnCart } from "@store/slices/cartSlice/actions/setShippingMethodsOnCart";
import { fetchFreeTrialOrderDeliveryLimits } from "@store/slices/LEGACY__checkoutSlice/actions/fetchOrderDeliveryLimits";
import { getFreeTrialDefaultShippingMethods } from "@store/slices/LEGACY__checkoutSlice/actions/getDefaultShippingMethods";
import { getPGSToken } from "@store/slices/LEGACY__checkoutSlice/actions/getPGSToken";
import { placeFreeTrialOrder } from "@store/slices/LEGACY__checkoutSlice/actions/placeOrder";
import { fetchContactEmail } from "@store/slices/LEGACY__orderSlice/actions/fetchContactEmail";
import { fetchFreeTrialOrder } from "@store/slices/LEGACY__orderSlice/actions/fetchOrder";
import { fetchFreeTrialOrderPoints } from "@store/slices/LEGACY__orderSlice/actions/fetchOrderPoints";
import { fetchPaymentMethod } from "@store/slices/LEGACY__orderSlice/actions/fetchPaymentMethod";
import type { FreeTrialCart, OrderException } from "@store/types";
import { mapFreeTrialPriceFromCartPrices } from "@store/utils";
import {
  redirectToTrialCheckoutPage,
  removeFreeTrialRefreshProtection,
  setFreeTrialRedirect,
} from "@store/utils/cartHelpers";
import { fromMagentoCurrency } from "@store/utils/fromMagentoCurrency";
import { mapFreeTrialPriceFromOrder } from "@store/utils/mapFreeTrialPriceFromOrder";
import { parseAddToCartErrorMessage } from "@store/utils/parseAddToCartErrorMessage";

import { getMessageBus } from "@jti/fe-message-bus";

const messageBus = getMessageBus();

export const initialFreeTrialCartState: FreeTrialCart = {
  id: "",
  items: [],
  isTrialEligible: undefined,
  prices: {
    fetchStatus: "idle",
    data: {
      currency: "",
      subtotal: 0,
      shipping: 0,
      total: 0,
      deposit: 0,
      totalNowTax: [],
      totalAfterTrial: 0,
      totalAfterTrialInclTax: 0,
      totalAfterTrialTax: [],
    },
  },
  areItemsLoading: false,
  page: "",
  checkout: {
    orderDeliveryLimits: {
      fetchStatus: "idle",
      data: { mode: "", limits: [] },
    },
    shippingMethods: {
      fetchStatus: "idle",
      data: [],
    },
    setShippingMethodsOnCartPending: false,
    orderPending: false,
    selectedAddressForPayment: undefined,
    paymentToken: "",
    hasOrderError: false,
    error: {
      exception: null,
      visible: false,
    },
  },
  order: {
    orderDetails: {
      fetchStatus: "idle",
      data: null,
    },
    points: 0,
  },
  currency: {
    code: "",
    symbol: "",
  },
  isZeroValueOrder: false,
};

export const freeTrialCartSlice: Slice<FreeTrialCart> = createSlice({
  name: "freeTrialCart",
  initialState: initialFreeTrialCartState,

  reducers: {
    showError(state: Draft<FreeTrialCart>, action: PayloadAction<OrderException>) {
      state.checkout.error = {
        exception: action.payload,
        visible: true,
      };
    },
    hideError(state: Draft<FreeTrialCart>) {
      state.checkout.error = {
        exception: null,
        visible: true,
      };
    },
  },

  extraReducers: (builder) => {
    builder.addCase(fetchTrialCart.fulfilled, (state, action) => {
      if (action.payload.id) {
        state.id = action.payload.id;
        state.isTrialEligible = action.payload.is_trial_eligible;
      }
    });

    builder.addCase(addFreeTrialProductToCart.fulfilled, () => {
      removeFreeTrialRefreshProtection();
      setFreeTrialRedirect();
      redirectToTrialCheckoutPage();
    });
    builder.addCase(addFreeTrialProductToCart.rejected, (state, action) => {
      action.payload &&
        messageBus.publish("fail.cart.add", {
          payload: { message: parseAddToCartErrorMessage(action.payload), requestId: action.meta.arg.requestId },
        });
    });

    builder.addCase(fetchTrialCartItems.fulfilled, (state, action) => {
      state.items = action.payload.items;
    });

    builder.addCase(fetchTrialCartPrices.rejected, (state) => {
      state.prices.fetchStatus = "error";
    });
    builder.addCase(fetchTrialCartPrices.pending, (state) => {
      state.prices.fetchStatus = "pending";
    });
    builder.addCase(fetchTrialCartPrices.fulfilled, (state, action) => {
      state.prices.data = mapFreeTrialPriceFromCartPrices(action.payload.cart);
      state.prices.fetchStatus = "success";
    });

    builder.addCase(fetchFreeTrialOrderDeliveryLimits.pending, (state) => {
      state.checkout.orderDeliveryLimits.fetchStatus = "pending";
    });

    builder.addCase(fetchFreeTrialOrderDeliveryLimits.rejected, (state) => {
      state.checkout.orderDeliveryLimits.fetchStatus = "error";
    });

    builder.addCase(fetchFreeTrialOrderDeliveryLimits.fulfilled, (state, action) => {
      state.checkout.orderDeliveryLimits.fetchStatus = "success";
      state.checkout.orderDeliveryLimits.data = action.payload;
    });

    builder.addCase(getFreeTrialDefaultShippingMethods.pending, (state) => {
      state.checkout.shippingMethods.fetchStatus = "pending";
    });

    builder.addCase(getFreeTrialDefaultShippingMethods.rejected, (state) => {
      state.checkout.shippingMethods.fetchStatus = "error";
    });

    builder.addCase(getFreeTrialDefaultShippingMethods.fulfilled, (state, action) => {
      state.checkout.shippingMethods.fetchStatus = "success";
      state.checkout.shippingMethods.data = action.payload.methods;
    });

    builder.addCase(setFreeTrialShippingMethodsOnCart.pending, (state) => {
      state.checkout.setShippingMethodsOnCartPending = true;
    });
    builder.addCase(setFreeTrialShippingMethodsOnCart.fulfilled, (state) => {
      state.checkout.setShippingMethodsOnCartPending = false;
    });

    builder.addCase(placeFreeTrialOrder.rejected, (state) => {
      state.checkout.orderPending = false;
      state.checkout.hasOrderError = true;
    });
    builder.addCase(placeFreeTrialOrder.pending, (state) => {
      state.checkout.orderPending = true;
    });
    builder.addCase(placeFreeTrialOrder.fulfilled, (state, action) => {
      state.page = "order";
      state.id = "";
      state.items = [];
      state.checkout.orderPending = false;
      state.checkout.selectedAddressForPayment = action.payload.shippingAddress;
      state.order.orderNumber = action.payload.orderNumber;
    });

    builder.addCase(getPGSToken.pending, (state) => {
      state.checkout.orderPending = true;
    });
    builder.addCase(getPGSToken.fulfilled, (state, action) => {
      state.checkout.orderPending = false;
      state.checkout.paymentToken = action.payload;
    });
    builder.addCase(getPGSToken.rejected, (state, action) => {
      state.checkout.error.exception = action.payload || null;
      state.checkout.error.visible = true;
    });

    builder.addCase(fetchFreeTrialOrder.rejected, (state) => {
      state.order.orderDetails.fetchStatus = "error";
    });
    builder.addCase(fetchFreeTrialOrder.pending, (state) => {
      state.order.orderDetails.fetchStatus = "pending";
    });
    builder.addCase(fetchFreeTrialOrder.fulfilled, (state, action) => {
      state.order.orderDetails.fetchStatus = "success";
      state.order.orderDetails.data = action.payload;

      state.prices.data = mapFreeTrialPriceFromOrder(action.payload); // TODO: investigate if this is needed
    });

    builder.addCase(fetchPaymentMethod.fulfilled, (state, action) => {
      state.order.paymentMethod = action.payload;
    });

    builder.addCase(fetchContactEmail.fulfilled, (state, action) => {
      state.order.contactEmail = action.payload.personalInfo.email;
    });

    builder.addCase(fetchFreeTrialOrderPoints.fulfilled, (state, action) => {
      state.order.points = action.payload;
    });

    builder.addCase(fetchCurrency.fulfilled, (state, action) => {
      state.currency = fromMagentoCurrency(action.payload);
    });
  },
});
